import React from 'react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { PlantafelDisplaySettingsForm } from '../../../components/plantafel/PlantafelWeekdaySettingsForm';
import {
  DEFAULT_PLANTAFEL_DISPLAY_SETTINGS,
  normalizePlantafelDisplaySettings,
} from '../../../utils/plantafelSettings';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { SETTINGS_BTN_PRIMARY } from '../utils/settingsSectionStyles';
import { settingsValuesEqual } from '../utils/settingsDirty';

export function PlantafelSettingsSection() {
  const [display, setDisplay] = React.useState(DEFAULT_PLANTAFEL_DISPLAY_SETTINGS);
  const [baseline, setBaseline] = React.useState(DEFAULT_PLANTAFEL_DISPLAY_SETTINGS);
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    fetch('/api/settings/plantafel', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        setDisplay(normalizePlantafelDisplaySettings(data));
        setBaseline(normalizePlantafelDisplaySettings(data));
      })
      .catch(() => {
        if (!cancelled) setError('Plantafel-Einstellungen konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/settings/plantafel', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          visible_weekdays: display.visibleWeekdays,
          day_hour_start: display.dayHours.hourStart,
          day_hour_end: display.dayHours.hourEnd,
          default_activity_duration_minutes: display.defaultActivityDurationMinutes,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Speichern fehlgeschlagen.');
      }
      const data = (await res.json()) as {
        visible_weekdays?: unknown;
        day_hour_start?: unknown;
        day_hour_end?: unknown;
        default_activity_duration_minutes?: unknown;
      };
      const next = normalizePlantafelDisplaySettings(data);
      setDisplay(next);
      setBaseline(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      <SettingsCard title="Plantafel">
        {!loaded ? (
          <InlineLoadingState label="Lade…" className="justify-start" />
        ) : (
          <div className="space-y-4">
            <PlantafelDisplaySettingsForm
              weekdays={display.visibleWeekdays}
              onWeekdaysChange={(visibleWeekdays) =>
                setDisplay((prev) => ({ ...prev, visibleWeekdays }))
              }
              dayHours={display.dayHours}
              onDayHoursChange={(dayHours) => setDisplay((prev) => ({ ...prev, dayHours }))}
              defaultActivityDurationMinutes={display.defaultActivityDurationMinutes}
              onDefaultActivityDurationMinutesChange={(defaultActivityDurationMinutes) =>
                setDisplay((prev) => ({ ...prev, defaultActivityDurationMinutes }))
              }
              disabled={saving}
            />
            {error ? (
              <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
            ) : null}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving || settingsValuesEqual(display, baseline)}
                className={SETTINGS_BTN_PRIMARY}
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
              <SettingsCardSavedStatus saved={saved} />
            </div>
          </div>
        )}
      </SettingsCard>
    </div>
  );
}
